import { Chip, Collapse, Icon, IconButton, Paper, Typography } from '@shared/ui';
import { useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { ModeEvaluationEntity, TimelineWidgetProps } from '../types';

export interface ModeEvaluationWidgetProps extends TimelineWidgetProps<ModeEvaluationEntity> {
  debugMode?: boolean;
}

// Register renderer + visibility
registerWidgetRenderer(
  'mode_evaluation',
  ({ entity, debugMode }) => (
    <ModeEvaluationWidget key={(entity as any).id} entity={entity as any} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);

export function ModeEvaluationWidget({ entity, debugMode = false }: ModeEvaluationWidgetProps) {
  const { status, currentMode, analysis, decision, recommendedMode } = entity.props;
  const [expanded, setExpanded] = useState(true);

  const statusColor = status === 'completed' ? 'success' : status === 'error' ? 'error' : 'primary';
  const decisionColor = decision === 'switch' ? 'warning' : 'success';

  return (
    <Paper variant="outlined" className="p-4 bg-gradient-to-r from-amber-50 to-orange-50">
      <div className="flex items-start gap-3">
        <Icon sx={{ fontSize: 24, color: `${statusColor}.main` }}>
          {status === 'active'
            ? 'assessment'
            : status === 'completed'
              ? 'check_circle'
              : 'error_outline'}
        </Icon>

        <div className="flex-1">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              🔍 Mode Evaluation
            </Typography>
            {currentMode ? (
              <Chip label={currentMode} size="small" color="primary" variant="outlined" />
            ) : null}
            {decision ? (
              <Chip
                label={decision === 'switch' ? 'Switch Recommended' : 'Continue'}
                size="small"
                color={decisionColor}
                variant="outlined"
              />
            ) : null}
            <IconButton size="small" sx={{ ml: 'auto' }}>
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          <Collapse in={expanded}>
            {status === 'active' || status === 'completed' ? (
              <div className="mt-3 space-y-3">
                {analysis ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography variant="subtitle2" className="mb-1">
                      Analysis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {analysis}
                    </Typography>
                  </div>
                ) : null}

                {decision === 'switch' && recommendedMode ? (
                  <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                    <Typography variant="subtitle2" className="mb-1">
                      Recommended Mode
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="warning.main">
                      {recommendedMode}
                    </Typography>
                  </div>
                ) : null}

                {status === 'completed' && entity.props.error ? (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <Typography variant="body2" color="error">
                      Error: {entity.props.error}
                    </Typography>
                  </div>
                ) : null}

                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}

            {status === 'error' ? (
              <div className="mt-3">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <Typography variant="body2" color="error">
                    {entity.props.error || 'An error occurred while evaluating mode'}
                  </Typography>
                </div>
                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2 mt-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}
